import { type QueryKey, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import {
	type PlaceholderValues,
	useAppTranslation,
} from 'hooks/use-translates';

import { useApiRedirect } from '@login/hooks/use-api-redirect';

type IdmPageResponse = {
	action: 'Redirect' | 'Display';
	placeholders?: PlaceholderValues | null;
	xsrfToken?: string | null;
	redirectUrl?: string | null;
};

type UseIdmPageOptions<TData extends IdmPageResponse> = {
	queryKey: QueryKey;
	queryFn: () => Promise<TData>;
	enabled?: boolean;
	/** Surfaces a fetch failure; defaults to nothing so callers opt in. */
	onError?: (error: unknown) => void;
	/**
	 * Signout returns its target as `postLogoutRedirectUri` rather than
	 * `redirectUrl`.
	 */
	selectRedirectUrl?: (data: TData) => string | null | undefined;
};

const defaultSelectRedirectUrl = (data: IdmPageResponse) => data.redirectUrl;

/**
 * Standard shape of an IDM-backed page: fetch the page payload, follow a
 * `Redirect` action, and expose a translator bound to the page's placeholders.
 *
 * The redirect runs in an effect. Every page previously called `goToRedirect`
 * straight from its render body, which re-fired on each render and double-fired
 * under `reactStrictMode`.
 */
export const useIdmPage = <TData extends IdmPageResponse>({
	queryKey,
	queryFn,
	enabled = true,
	onError,
	selectRedirectUrl = defaultSelectRedirectUrl,
}: UseIdmPageOptions<TData>) => {
	const { goToRedirect } = useApiRedirect();

	const {
		data: pageData,
		error,
		isLoading,
		isFetched,
	} = useQuery({ queryKey, queryFn, enabled });

	useEffect(() => {
		if (error) onError?.(error);
	}, [error, onError]);

	useEffect(() => {
		if (!pageData) return;
		goToRedirect({
			action: pageData.action,
			url: selectRedirectUrl(pageData),
		});
	}, [pageData, goToRedirect, selectRedirectUrl]);

	const { t, i18n } = useAppTranslation(pageData?.placeholders);

	return useMemo(
		() => ({
			pageData,
			error,
			isLoading,
			isFetched,
			/** True once the payload says this page should render. */
			shouldDisplay: pageData?.action === 'Display',
			token: pageData?.xsrfToken,
			t,
			i18n,
		}),
		[pageData, error, isLoading, isFetched, t, i18n]
	);
};

export default useIdmPage;
